import RequireOwner from "@/components/RequireOwner";
import BusinessCardResponsive from "@/components/business/BusinessCardResponsive";
import { useOnboarding } from "@/components/owner/OnboardingProvider";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import StepHeader from "@/components/owner/StepHeader";
import {
  buildBusinessPublicCardLink,
  deleteDraftBusiness,
  getSavedBusinessValidationRequirements,
  persistOwnerOnboardingStep,
  readBusiness,
  SAVED_BUSINESS_INCOMPLETE_MESSAGE,
  submitBusinessForReview,
} from "@/lib/onboardingBusiness";
import {
  normalizeOwnerOnboardingStatus,
  resolveOwnerOnboardingAccess,
} from "@/lib/onboardingResume";
import type { Business } from "@/lib/types";
import {
  getOnboardingMissingRequirements,
  validateAll,
} from "@/lib/validation";
import * as Clipboard from "expo-clipboard";
import { router, useLocalSearchParams } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

type ReviewMode = "onboarding" | "edit-approved";

function getMode(value: unknown): ReviewMode {
  return value === "edit-approved" ? "edit-approved" : "onboarding";
}

function isLiveBusiness(data: Business | null) {
  const status = normalizeOwnerOnboardingStatus((data as any)?.status);

  return (
    status === "approved" &&
    (data as any)?.isPublished === true &&
    (data as any)?.isDeleted !== true &&
    (data as any)?.isDeactivated !== true
  );
}

export default function Review() {
  const { user } = useOwnerAuth();
  const { businessId, setBusinessId, correctionAuthorized } = useOnboarding();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const params = useLocalSearchParams<{
    businessId?: string;
    mode?: string;
  }>();

  const paramBusinessId =
    typeof params.businessId === "string" ? params.businessId : undefined;

  const mode = getMode(params.mode);

  const effectiveBusinessId = paramBusinessId || businessId || null;

  const [data, setData] = useState<Business | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedMissingRequirements, setSavedMissingRequirements] = useState<
    string[]
  >([]);

  const businessIdRef = useRef<string | null>(null);
  const scrollRef = useRef<ScrollView | null>(null);

  useEffect(() => {
    businessIdRef.current = effectiveBusinessId;
  }, [effectiveBusinessId]);

  useEffect(() => {
    setSavedMissingRequirements([]);
    setSubmitError(null);
    setSubmitted(false);
  }, [effectiveBusinessId]);

  useEffect(() => {
    if (!user) return;
    if (!paramBusinessId) return;
    if (businessId === paramBusinessId) return;

    setBusinessId(paramBusinessId);
  }, [user, paramBusinessId, businessId, setBusinessId]);

  useEffect(() => {
    if (!user) {
      router.replace("/(owner)/onboarding/signin" as any);
      return;
    }

    if (!effectiveBusinessId) {
      router.replace("/(owner)/start" as any);
    }
  }, [user, effectiveBusinessId]);

  const refresh = useCallback(async (id: string) => {
    const d = await readBusiness(id);
    const raw = d as (Business & {
      status?: unknown;
      isPublished?: boolean;
      isDeleted?: boolean;
    }) | null;
    const access = resolveOwnerOnboardingAccess({
      status: raw?.status,
      isPublished: raw?.isPublished,
      isDeleted: raw?.isDeleted,
      approvedEditMode: mode === "edit-approved",
      correctionAuthorized,
    });

    if (access !== "edit") {
      router.replace("/(owner)/dashboard" as any);
      return;
    }
    setData(d as Business);
  }, [mode, correctionAuthorized]);

  useEffect(() => {
    if (!user || !effectiveBusinessId) return;

    (async () => {
      try {
        await refresh(effectiveBusinessId);
      } catch (e: any) {
        console.warn("Business review load failed.");
        Alert.alert("Could not load", e?.message || "Unknown error");
      }
    })();
  }, [user, effectiveBusinessId, refresh]);

  const validation = useMemo(() => validateAll((data || {}) as any), [data]);
  const missingRequirements = useMemo(
    () => getOnboardingMissingRequirements(validation),
    [validation]
  );
  const hasSavedValidationError = savedMissingRequirements.length > 0;
  const displayedMissingRequirements = hasSavedValidationError
    ? savedMissingRequirements
    : missingRequirements;

  const status = normalizeOwnerOnboardingStatus((data as any)?.status);
  const isPending = status === "pending";
  const isLive = isLiveBusiness(data);

  const isApprovedEditMode = mode === "edit-approved";
  const slugForPublic = String((data as any)?.slug || "").trim();
  const publicLink = isLive && slugForPublic
    ? buildBusinessPublicCardLink(slugForPublic)
    : "";

  const rejectedReason = String((data as any)?.rejectedReason || "").trim();

  const changesRequestMessage = String(
    (data as any)?.changesRequestMessage || ""
  ).trim();

  const canSubmit =
    status === "draft" ||
    status === "rejected" ||
    status === "changes_requested";

  const canNavigateBackToItems = isApprovedEditMode || canSubmit;

  const canDeleteDraft = status === "draft";

  const goDashboard = () => {
    setBusinessId(null);
    router.replace("/(owner)/dashboard" as any);
  };

  const showSubmitSuccess = () => {
    const title = "Submitted";
    const message = "Your business has been submitted for review.";

    if (
      Platform.OS === "web" &&
      typeof window !== "undefined" &&
      typeof window.alert === "function"
    ) {
      window.alert(`${title}\n\n${message}`);
      goDashboard();
      return;
    }

    Alert.alert(title, message, [
      {
        text: "OK",
        onPress: goDashboard,
      },
    ]);
  };

  const goFix = (pathname: string) => {
    const id = businessIdRef.current;

    if (!id) {
      router.push("/(owner)/start" as any);
      return;
    }

    router.push({
      pathname,
      params: {
        businessId: id,
        mode: isApprovedEditMode ? "edit-approved" : "onboarding",
      },
    } as any);
  };

  const scrollToValidationSummary = () => {
    const scrollToTop = () => {
      scrollRef.current?.scrollTo({ y: 0, animated: true });
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(scrollToTop);
    } else {
      setTimeout(scrollToTop, 0);
    }
  };

  const submit = async () => {
    const id = businessIdRef.current;
    if (!id) {
      setSubmitError("BusinessId is missing. Go back and try again.");
      return;
    }

    if (busy) return;

    setSubmitError(null);
    setSavedMissingRequirements([]);

    if (isApprovedEditMode) {
      goDashboard();
      return;
    }

    if (!canSubmit) {
      setSubmitError(
        isPending
          ? "This business is already pending admin review."
          : "This business cannot be submitted from this screen."
      );
      Alert.alert(
        "Cannot submit",
        isPending
          ? "This business is already pending admin review."
          : "This business cannot be submitted from this screen."
      );
      return;
    }

    if (!validation.ok) {
      setSubmitted(true);
      setSubmitError("Please fix the highlighted fields before continuing.");
      scrollToValidationSummary();
      return;
    }

    setBusy(true);

    try {
      await submitBusinessForReview(id);

      showSubmitSuccess();
    } catch (e: any) {
      const savedRequirements = getSavedBusinessValidationRequirements(e);

      if (savedRequirements.length > 0) {
        setSubmitted(true);
        setSavedMissingRequirements(savedRequirements);
        setSubmitError(SAVED_BUSINESS_INCOMPLETE_MESSAGE);
        scrollToValidationSummary();
        return;
      }

      const message = String(e?.message || "");
      const code = String(e?.code || "");
      const permissionDenied =
        code.includes("permission-denied") ||
        message.toLowerCase().includes("permission-denied");
      const ownerAllowlistBlocked = message
        .toLowerCase()
        .includes("owners cannot update");

      const visibleMessage = permissionDenied
        ? "Permission rules blocked this submit. Please confirm the business is still editable, then try again."
        : ownerAllowlistBlocked
        ? "We couldn't submit your business right now. Please refresh Review and try again."
        : message || "Unknown error";

      setSubmitError(visibleMessage);
      Alert.alert("Submit failed", visibleMessage);
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async () => {
    const id = businessIdRef.current;
    if (!id) return;

    if (busy) return;

    if (!user) {
      Alert.alert("Missing user", "Please sign in again and try.");
      return;
    }

    if (!canDeleteDraft) {
      Alert.alert(
        "Cannot delete",
        "Only draft businesses can be deleted by the owner."
      );
      return;
    }

    Alert.alert("Delete draft?", "This will soft-delete this draft business.", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          setBusy(true);

          try {
            await deleteDraftBusiness(id, user.uid);
            await persistOwnerOnboardingStep("basics");
            setBusinessId(null);
            router.replace("/(owner)/start" as any);
          } catch (e: any) {
            Alert.alert("Delete failed", e?.message || "Unknown error");
          } finally {
            setBusy(false);
          }
        },
      },
    ]);
  };

  const openLive = () => {
    if (!slugForPublic || !isLive) {
      Alert.alert(
        "Card is not public",
        "This business card is not currently public."
      );
      return;
    }

    router.push({
      pathname: "/p/[slug]",
      params: { slug: slugForPublic },
    } as any);
  };

  const copyLink = async () => {
    if (!publicLink) {
      Alert.alert(
        "No public link yet",
        "This business does not have a public slug yet."
      );
      return;
    }

    try {
      await Clipboard.setStringAsync(publicLink);
      Alert.alert("Copied", "The public card link has been copied.");
    } catch (error: any) {
      Alert.alert("Copy failed", error?.message || "Could not copy the link.");
    }
  };

  const headerTitle = isApprovedEditMode
    ? "Manage Business Profile"
    : isLive
    ? "Step 4: Review (Live)"
    : "Step 4: Review & Submit";

  const headerSubtitle = isApprovedEditMode
    ? "Review your business card and make any final updates."
    : isLive
    ? "Your card is live. Open it to confirm everything looks good."
    : isPending
    ? "Your business is pending admin review. You cannot edit it until admin responds, unless you withdraw it from your dashboard."
    : "Review your business card, then submit it for admin review.";

  const primaryLabel = busy
    ? isApprovedEditMode
      ? "Saving..."
      : "Submitting..."
    : isApprovedEditMode
    ? "Save & Return to Dashboard"
    : !data
    ? "Loading..."
    : isLive
    ? "Live"
    : isPending
    ? "Already pending review"
    : "Submit for Review";

  const primaryDisabled =
    busy ||
    !data ||
    (!isApprovedEditMode && (isLive || isPending || !canSubmit));

  const feedbackTitle =
    status === "changes_requested" ? "Changes requested" : "Rejected";

  const feedbackMessage =
    status === "changes_requested"
      ? changesRequestMessage || "Admin requested changes. Please fix and resubmit."
      : rejectedReason || "No reason provided.";

  return (
    <RequireOwner>
      <View style={{ flex: 1, backgroundColor: "#fff" }}>
        {isDesktop ? (
          <View style={desktopHeaderRow}>
            <View style={{ flex: 1 }}>
              <Text style={desktopTitle}>{headerTitle}</Text>
              <Text style={desktopTitleSubtitle}>{headerSubtitle}</Text>
            </View>

            <View style={desktopHeaderActions}>
              {canNavigateBackToItems ? (
                <Pressable
                  onPress={() => goFix("/(owner)/onboarding/items")}
                  style={headerBtnLight}
                >
                  <Text style={{ fontWeight: "900" }}>Back to Items</Text>
                </Pressable>
              ) : null}

              <Pressable
                onPress={submit}
                disabled={primaryDisabled}
                style={[headerBtnDark, primaryDisabled ? { opacity: 0.5 } : null]}
              >
                <Text style={{ color: "#fff", fontWeight: "900" }}>
                  {primaryLabel}
                </Text>
              </Pressable>

              {isApprovedEditMode ? (
                <>
                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/basics")}
                    style={headerBtnLight}
                  >
                    <Text style={{ fontWeight: "900" }}>Edit details</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/brand")}
                    style={headerBtnLight}
                  >
                    <Text style={{ fontWeight: "900" }}>Brand & Links</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/items")}
                    style={headerBtnLight}
                  >
                    <Text style={{ fontWeight: "900" }}>Items</Text>
                  </Pressable>
                </>
              ) : null}

              {!isApprovedEditMode && !isPending && !isLive ? (
                <Pressable
                  onPress={() => goFix("/(owner)/onboarding/basics")}
                  style={headerBtnLight}
                >
                  <Text style={{ fontWeight: "900" }}>Edit</Text>
                </Pressable>
              ) : null}

              {canDeleteDraft && !isApprovedEditMode ? (
                <Pressable
                  onPress={deleteDraft}
                  disabled={busy}
                  style={[
                    headerBtnLight,
                    { borderColor: "#fecaca", opacity: busy ? 0.6 : 1 },
                  ]}
                >
                  <Text style={{ fontWeight: "900", color: "#b91c1c" }}>
                    Delete
                  </Text>
                </Pressable>
              ) : null}
            </View>
          </View>
        ) : (
          <StepHeader
            title={headerTitle}
            subtitle={headerSubtitle}
            showBack={canNavigateBackToItems}
            onBack={() => goFix("/(owner)/onboarding/items")}
          />
        )}

        {submitError ? (
          <View style={submitErrorWrap}>
            <Text style={submitErrorText}>{submitError}</Text>
          </View>
        ) : null}

        <ScrollView
          ref={scrollRef}
          contentContainerStyle={[
            baseContent,
            isDesktop ? desktopContent : mobileContent,
          ]}
          showsVerticalScrollIndicator={isDesktop}
        >
          <View style={previewWrap}>
            {data &&
            !isApprovedEditMode &&
            canSubmit &&
            (!validation.ok || hasSavedValidationError) ? (
              <View
                style={[
                  missingBox,
                  submitted ? missingBoxHighlighted : null,
                ]}
              >
                {submitted ? (
                  <View style={topErrorBox}>
                    <Text style={topErrorText}>
                      {hasSavedValidationError
                        ? SAVED_BUSINESS_INCOMPLETE_MESSAGE
                        : "Please fix the highlighted fields before continuing."}
                    </Text>
                  </View>
                ) : null}

                <Text style={missingTitle}>Checklist before Submit</Text>

                <View style={{ gap: 4 }}>
                  {displayedMissingRequirements.map((item, index) => (
                    <Text key={`${index}-${item}`} style={missingText}>
                      - {item}
                    </Text>
                  ))}
                </View>

                <View style={rowActions}>
                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/basics")}
                    style={btnLightSmall}
                  >
                    <Text style={{ fontWeight: "900" }}>Edit details</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/brand")}
                    style={btnLightSmall}
                  >
                    <Text style={{ fontWeight: "900" }}>Brand & links</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/items")}
                    style={btnLightSmall}
                  >
                    <Text style={{ fontWeight: "900" }}>Items</Text>
                  </Pressable>
                </View>
              </View>
            ) : null}

            <View style={previewCard}>
              {data ? (
                <BusinessCardResponsive business={data} />
              ) : (
                <Text style={{ color: "#6b7280" }}>Loading preview...</Text>
              )}
            </View>

            {isPending && !isLive ? (
              <View style={pendingBox}>
                <Text style={{ fontWeight: "900" }}>Submitted</Text>
                <Text style={{ color: "#374151", lineHeight: 20 }}>
                  Your business is pending admin review. Use the dashboard if
                  you need to withdraw it before approval.
                </Text>

                <Pressable onPress={goDashboard} style={btnLightSmall}>
                  <Text style={{ fontWeight: "900" }}>Go to Dashboard</Text>
                </Pressable>
              </View>
            ) : null}

            {(status === "rejected" || status === "changes_requested") &&
            !isLive ? (
              <View style={rejectedBox}>
                <Text style={{ fontWeight: "900" }}>{feedbackTitle}</Text>
                <Text style={{ color: "#374151", lineHeight: 20 }}>
                  {feedbackMessage}
                </Text>

                <View style={rowActions}>
                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/basics")}
                    style={btnLightSmall}
                  >
                    <Text style={{ fontWeight: "900" }}>Fix details</Text>
                  </Pressable>
                </View>
              </View>
            ) : null}

            {isApprovedEditMode ? (
              <View style={approvedBox}>
                {publicLink ? (
                  <Text style={{ color: "#6b7280", fontSize: 12 }}>
                    {publicLink}
                  </Text>
                ) : null}

                <View style={rowActions}>
                  {isLive ? (
                    <Pressable onPress={openLive} style={btnDarkSmall}>
                      <Text style={{ color: "#fff", fontWeight: "900" }}>
                        Open card
                      </Text>
                    </Pressable>
                  ) : null}

                  <Pressable onPress={copyLink} style={btnLightSmall}>
                    <Text style={{ fontWeight: "900" }}>Copy link</Text>
                  </Pressable>

                  <Pressable onPress={goDashboard} style={btnLightSmall}>
                    <Text style={{ fontWeight: "900" }}>Dashboard</Text>
                  </Pressable>
                </View>
              </View>
            ) : null}

            {isLive && !isApprovedEditMode ? (
              <View style={liveBox}>
                <Text style={{ fontWeight: "900" }}>Live</Text>

                <Text style={{ color: "#374151", lineHeight: 20 }}>
                  Your card is public. Open it to confirm everything looks good.
                </Text>

                {publicLink ? (
                  <Text style={{ color: "#6b7280", fontSize: 12 }}>
                    {publicLink}
                  </Text>
                ) : null}

                <View style={rowActions}>
                  <Pressable onPress={openLive} style={btnDarkSmall}>
                    <Text style={{ color: "#fff", fontWeight: "900" }}>
                      Open card
                    </Text>
                  </Pressable>

                  <Pressable onPress={copyLink} style={btnLightSmall}>
                    <Text style={{ fontWeight: "900" }}>Copy link</Text>
                  </Pressable>
                </View>
              </View>
            ) : null}
          </View>
        </ScrollView>

        {!isDesktop ? (
          <View style={bottomBar}>
            <Pressable
              onPress={submit}
              disabled={primaryDisabled}
              style={[btnDark, primaryDisabled ? { opacity: 0.5 } : null]}
            >
              <Text style={{ color: "#fff", fontWeight: "900" }}>
                {primaryLabel}
              </Text>
            </Pressable>

            <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
              {isApprovedEditMode ? (
                <>
                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/basics")}
                    style={[btnAction, { flex: 1 }]}
                  >
                    <Text style={{ fontWeight: "900" }}>Details</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/brand")}
                    style={[btnAction, { flex: 1 }]}
                  >
                    <Text style={{ fontWeight: "900" }}>Links</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => goFix("/(owner)/onboarding/items")}
                    style={[btnAction, { flex: 1 }]}
                  >
                    <Text style={{ fontWeight: "900" }}>Items</Text>
                  </Pressable>
                </>
              ) : null}

              {!isApprovedEditMode && !isPending && !isLive ? (
                <Pressable
                  onPress={() => goFix("/(owner)/onboarding/basics")}
                  style={[btnAction, { flex: 1 }]}
                >
                  <Text style={{ fontWeight: "900" }}>Edit</Text>
                </Pressable>
              ) : null}

              {canDeleteDraft && !isApprovedEditMode ? (
                <Pressable
                  onPress={deleteDraft}
                  disabled={busy}
                  style={[
                    btnAction,
                    {
                      flex: 1,
                      borderColor: "#fecaca",
                      opacity: busy ? 0.6 : 1,
                    },
                  ]}
                >
                  <Text style={{ fontWeight: "900", color: "#b91c1c" }}>
                    Delete
                  </Text>
                </Pressable>
              ) : null}
            </View>
          </View>
        ) : null}
      </View>
    </RequireOwner>
  );
}

/* -------------------- styles -------------------- */

const baseContent = {
  padding: 16,
  gap: 12,
};

const mobileContent = {
  paddingBottom: 240,
};

const desktopContent = {
  paddingBottom: 32,
};

const previewWrap = {
  width: "100%" as const,
  maxWidth: 980,
  alignSelf: "center" as const,
  gap: 12,
};

const previewCard = {
  borderWidth: 1,
  borderColor: "#e5e7eb",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#fff",
  overflow: "visible" as const,
};

const pendingBox = {
  borderWidth: 1,
  borderColor: "#bfdbfe",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#eff6ff",
  gap: 8,
};

const rejectedBox = {
  borderWidth: 1,
  borderColor: "#fecaca",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#fef2f2",
  gap: 8,
};

const liveBox = {
  borderWidth: 1,
  borderColor: "#bbf7d0",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#f0fdf4",
  gap: 8,
};

const approvedBox = {
  borderWidth: 1,
  borderColor: "#bfdbfe",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#eff6ff",
  gap: 8,
};

const missingBox = {
  borderWidth: 1,
  borderColor: "#fed7aa",
  borderRadius: 18,
  padding: 12,
  backgroundColor: "#fff7ed",
  gap: 10,
};

const missingBoxHighlighted = {
  borderColor: "#ef4444",
};

const topErrorBox = {
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#fca5a5",
  backgroundColor: "#fef2f2",
  padding: 12,
};

const topErrorText = {
  color: "#991b1b",
  fontSize: 13,
  lineHeight: 18,
  fontWeight: "900" as const,
};

const submitErrorWrap = {
  marginHorizontal: 16,
  marginTop: 10,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#fca5a5",
  backgroundColor: "#fef2f2",
  padding: 12,
};

const submitErrorText = {
  color: "#991b1b",
  fontSize: 13,
  lineHeight: 18,
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const missingTitle = {
  color: "#111827",
  fontWeight: "900" as const,
};

const missingText = {
  color: "#7c2d12",
  lineHeight: 20,
  fontWeight: "700" as const,
};

const desktopHeaderRow = {
  paddingHorizontal: 16,
  paddingTop: 12,
  paddingBottom: 8,
  flexDirection: "row" as const,
  alignItems: "center" as const,
  gap: 12,
};

const desktopTitle = {
  fontSize: 22,
  fontWeight: "900" as const,
};

const desktopTitleSubtitle = {
  marginTop: 4,
  color: "#6b7280",
  fontWeight: "600" as const,
};

const desktopHeaderActions = {
  flexDirection: "row" as const,
  gap: 10,
  alignItems: "center" as const,
  flexWrap: "wrap" as const,
  justifyContent: "flex-end" as const,
};

const headerBtnDark = {
  height: 44,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 16,
};

const headerBtnLight = {
  height: 44,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#e5e7eb",
  backgroundColor: "#fff",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 16,
};

const bottomBar = {
  position: "absolute" as const,
  left: 0,
  right: 0,
  bottom: 0,
  padding: 12,
  gap: 10,
  borderTopWidth: 1,
  borderTopColor: "#e5e7eb",
  backgroundColor: "#fff",
};

const btnDark = {
  height: 45,
  borderRadius: 16,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const btnAction = {
  height: 40,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#e5e7eb",
  backgroundColor: "#fff",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
  marginBottom: 25,
};

const btnDarkSmall = {
  height: 44,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const btnLightSmall = {
  height: 44,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#e5e7eb",
  backgroundColor: "#fff",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const rowActions = {
  flexDirection: "row" as const,
  gap: 10,
  flexWrap: "wrap" as const,
};
